/* Kkumi shared money formatter (JS side).
 *
 * ONE canonical money formatter so asset scripts stop re-rolling
 * `'$' + (cents/100).toFixed(2)` (and drifting on units). The cents-vs-dollars
 * split is EXPLICIT in the method name so a caller can't pass the wrong unit:
 *   KKMoney.fromCents(1295)              -> "$12.95"
 *   KKMoney.fromCents(500,  {trim:true}) -> "$5"      (drop a whole-unit .00)
 *   KKMoney.fromCents(1295, {bare:true}) -> "12.95"   (no currency symbol)
 *   KKMoney.fromDollars(12.95)           -> "$12.95"
 *
 * Currency-aware: amounts are formatted in the shopper's ACTIVE (presentment)
 * currency, read from window.KKMoneyConfig which layout/theme.liquid emits
 * from `cart.currency.iso_code` — so a shopper browsing in yen sees
 * "¥3,000", not "$3000.00". Storefront prices (Liquid `variant.price`,
 * /products/*.js, /cart.js) are already converted by Shopify and always come
 * in subunits ×100 — even for zero-decimal currencies like JPY — so
 * fromCents always divides by 100; Intl then picks the currency's own
 * decimals.
 *
 * Amounts that come from the rewards worker (tier spend thresholds, voucher
 * values, greenlight/showdown prices) are in the SHOP's base currency and are
 * NOT converted — pass {shop:true} so they're labelled in that currency
 * rather than mislabelled as the shopper's.
 *
 * The other direction: anything that does maths against a shop-currency rule
 * (points per dollar, tier spend, a campaign's min_order_cents) must first
 * turn a storefront amount back into the shop's currency:
 *   KKMoney.toShopCents(250000)  // ¥2,500 at rate 97 -> 2577 (A$25.77)
 * Same subunit-×100 convention both sides. It uses Shopify.currency.rate
 * (shop -> active currency; content_for_header sets it on every storefront
 * page), so it is an estimate where a market has its own fixed prices.
 * Returns the amount unchanged when the shopper is in the shop's currency,
 * and null when they aren't and no usable rate exists — never a guess.
 *
 * And the display direction for a shop-currency amount the shopper should see
 * in THEIR currency (e.g. their store credit balance):
 *   KKMoney.fromShopCents(6000)  // A$60.00 at rate 97 -> "¥5,820"
 * Falls back to the shop-currency label ({shop:true}) when there is no usable
 * rate, so the amount is never mislabelled.
 *
 * Loaded head-defer from theme.liquid; formatters are called at render time
 * (after data loads), so window.KKMoney is always present by then.
 */
(function () {
  if (window.KKMoney) return;
  var cache = {};
  function cfg() { return window.KKMoneyConfig || {}; }
  function formatter(currency, locale, style, decimals) {
    var key = [currency, locale, style, decimals].join('|');
    if (cache[key]) return cache[key];
    var o = { style: style };
    if (style === 'currency') { o.currency = currency; o.currencyDisplay = 'narrowSymbol'; }
    if (decimals != null) { o.minimumFractionDigits = decimals; o.maximumFractionDigits = decimals; }
    var f;
    try { f = new Intl.NumberFormat(locale || undefined, o); }
    catch (e) {
      // Older engines without narrowSymbol: fall back to the plain symbol.
      if (o.currencyDisplay) o.currencyDisplay = 'symbol';
      try { f = new Intl.NumberFormat(locale || undefined, o); } catch (e2) { f = null; }
    }
    return (cache[key] = f);
  }
  function fmt(amount, opts) {
    opts = opts || {};
    if (!isFinite(amount)) amount = 0; // non-numeric input -> 0, never "NaN"
    var c = cfg();
    var currency = (opts.shop ? c.shopCurrency : c.currency) || c.shopCurrency;
    var base = currency && formatter(currency, c.locale, 'currency');
    if (base) {
      // The currency's own decimals (2 for AUD/USD, 0 for JPY/KRW).
      var digits = base.resolvedOptions().maximumFractionDigits;
      var whole = Math.round(amount * Math.pow(10, digits)) % Math.pow(10, digits) === 0;
      var d = (opts.trim && whole) ? 0 : digits;
      var f = formatter(currency, c.locale, opts.bare ? 'decimal' : 'currency', d);
      if (f) return f.format(amount);
    }
    // No config / no Intl: the original dollar formatting.
    var s = (opts.trim && amount % 1 === 0) ? amount.toFixed(0) : amount.toFixed(2);
    return opts.bare ? s : '$' + s;
  }
  function toShopCents(cents) {
    var n = Number(cents || 0);
    if (!isFinite(n)) n = 0;
    var c = cfg();
    if (!c.currency || !c.shopCurrency || c.currency === c.shopCurrency) return n;
    var sc = window.Shopify && window.Shopify.currency;
    var rate = (sc && sc.active === c.currency) ? parseFloat(sc.rate) : NaN;
    if (!(rate > 0)) return null;
    return Math.round(n / rate);
  }
  function fromShopCents(cents, opts) {
    var n = Number(cents || 0);
    if (!isFinite(n)) n = 0;
    var c = cfg();
    if (!c.currency || !c.shopCurrency || c.currency === c.shopCurrency) return fmt(n / 100, opts);
    var sc = window.Shopify && window.Shopify.currency;
    var rate = (sc && sc.active === c.currency) ? parseFloat(sc.rate) : NaN;
    if (!(rate > 0)) return fmt(n / 100, Object.assign({}, opts, { shop: true }));
    return fmt(Math.round(n * rate) / 100, opts);
  }
  window.KKMoney = {
    fromCents: function (cents, opts) { return fmt(Number(cents || 0) / 100, opts); },
    fromDollars: function (dollars, opts) { return fmt(Number(dollars || 0), opts); },
    toShopCents: toShopCents,
    fromShopCents: fromShopCents,
  };

  // The active currency is fixed at page load (KKMoneyConfig and
  // Shopify.currency come from Liquid), but a cart write can change it: with
  // no country picked, Shopify places the shopper by location, so a VPN or
  // travel switch mid-visit makes the next /cart/*.js answer in another
  // currency. Re-rendered sections then show it while everything formatted
  // here (cart editor prices, the store-credit note) kept the old one — a
  // yen page showed "$18.95 AUD" lines beside "¥19" and "¥6,766". When a
  // cartUpdate carries a cart (items + currency) in a different currency,
  // reload so Liquid, this config and the exchange rate agree again.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = event && event.cartData;
      var cur = cfg().currency;
      if (!cart || !Array.isArray(cart.items) || typeof cart.currency !== 'string' || !cur) return;
      if (cart.currency.toUpperCase() !== String(cur).toUpperCase()) window.location.reload();
    });
  }
})();
